'use client';

/**
 * Lit / écrit le numéro de page courant dans l'URL (search parameter `?page=`).
 *
 * - Lit la valeur depuis `useSearchParams()` ; toute valeur invalide
 *   (`page=0`, `page=-1`, `page=abc`, `page=`) est normalisée à 1.
 * - Écrit via `router.replace(..., { scroll: false })` pour ne pas polluer
 *   l'historique (chaque clic page rajouterait sinon une entrée back).
 * - `setPage(1)` retire le paramètre de l'URL (URL canonique sans bruit).
 * - `resetOnFiltersChange()` est l'helper appelé par les surfaces dès qu'un
 *   filtre / search / catégorie change — il efface `page` du searchParams
 *   tout en préservant le reste.
 */

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useMemo } from 'react';

const DEFAULT_PAGE_PARAM = 'page';

function normalizePage(raw: string | null): number {
    if (raw === null) return 1;
    const trimmed = raw.trim();
    if (trimmed.length === 0) return 1;
    const parsed = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return parsed;
}

export function usePageQueryParam(paramKey: string = DEFAULT_PAGE_PARAM): {
    page: number;
    setPage: (next: number) => void;
    resetOnFiltersChange: () => void;
} {
    const router = useRouter();
    const searchParams = useSearchParams();

    const page = useMemo(() => normalizePage(searchParams.get(paramKey)), [searchParams, paramKey]);

    const setPage = useCallback(
        (next: number) => {
            const target = Number.isFinite(next) && next >= 1 ? Math.floor(next) : 1;
            const params = new URLSearchParams(searchParams.toString());
            if (target === 1) {
                params.delete(paramKey);
            } else {
                params.set(paramKey, String(target));
            }
            const query = params.toString();
            router.replace(query.length > 0 ? `?${query}` : '?', { scroll: false });
        },
        [router, searchParams, paramKey],
    );

    const resetOnFiltersChange = useCallback(() => {
        const params = new URLSearchParams(searchParams.toString());
        if (!params.has(paramKey)) return;
        params.delete(paramKey);
        const query = params.toString();
        router.replace(query.length > 0 ? `?${query}` : '?', { scroll: false });
    }, [router, searchParams, paramKey]);

    return { page, setPage, resetOnFiltersChange };
}
